export interface GlossaryEntry {
  title: string;
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  fieldJoint: {
    title: "Field Joint",
    definition:
      "The pipe area around a weld where factory coating has been cut back and heating is required before the next field-applied process.",
  },
  inductionHeating: {
    title: "Induction Heating",
    definition:
      "The process of using a power source and coil configuration to heat the pipe steel at a field joint.",
  },
  powerSource: {
    title: "Power Source",
    definition:
      "A selectable induction heating power unit from the limited equipment catalogue.",
  },
  powerSourceRating: {
    title: "Power Source Rating",
    definition:
      "The apparent power rating of a power source, expressed in kVA.",
  },
  powerSourceFrequency: {
    title: "Power Source Frequency",
    definition:
      "The induction operating frequency of a power source, expressed in Hz and used as an advisory factor for heating speed and cooldown behaviour.",
  },
  powerSourceVoltage: {
    title: "Power Source Voltage",
    definition:
      "The calculated operating voltage expected for a recommended setup using the selected power source and coil configuration.",
  },
  maximumPowerSourceVoltage: {
    title: "Maximum Power Source Voltage",
    definition:
      "The rated maximum voltage a power source can provide, used as catalogue data and as an operating limit for recommended setups.",
  },
  powerSourceCurrent: {
    title: "Power Source Current",
    definition:
      "The calculated operating current expected for a recommended setup using the selected power source and coil configuration.",
  },
  maximumPowerSourceCurrent: {
    title: "Maximum Power Source Current",
    definition:
      "The rated maximum current a power source can provide, used as catalogue data and as an operating limit for recommended setups.",
  },
  powerFactor: {
    title: "Power Factor",
    definition:
      "The calculated ratio between delivered useful heating power and apparent power for a recommended setup.",
  },
  powerSourceOutputLimiting: {
    title: "Power Source Output Limiting",
    definition:
      "The adjustment of a recommended setup's calculated operating point so Power Source Voltage and Power Source Current do not exceed the selected power source's rated maximum values.",
  },
  referenceFrequency: {
    title: "Reference Frequency",
    definition:
      "The baseline power source frequency used when adjusting heating cycle time and cooldown time for frequency effects.",
  },
  frequencyHeatingFactor: {
    title: "Frequency Heating Factor",
    definition:
      "The ratio applied to the advisory heating cycle time estimate to account for the selected power source frequency relative to the reference frequency. A factor below 1 reduces estimated heating time; above 1 increases it.",
  },
  coilConfiguration: {
    title: "Coil Configuration",
    definition:
      "A coil arrangement for field joint heating assembled from a coil frame and bottom end, with calculated geometry and a compatible power source.",
  },
  optimumCoilRecommendation: {
    title: "Optimum Coil Recommendation",
    definition:
      "The calculated coil characteristics expected to best suit a field joint heating case, whether or not those characteristics are available as a standard coil configuration.",
  },
  catalogueMatchedCoilConfiguration: {
    title: "Catalogue-Matched Coil Configuration",
    definition:
      "The standard coil configuration selected from the standard equipment catalogue as the practical available match for the field joint heating case.",
  },
  coilBuildingBlock: {
    title: "Coil Building Block",
    definition:
      "A standard manufactured coil part that can be used to assemble a coil configuration.",
  },
  bottomEnd: {
    title: "Bottom End",
    definition:
      "A coil building block that supports a particular number of coil turns and contact spacing and is designed to work with any standard coil frame size.",
  },
  contactSpacing: {
    title: "Contact Spacing",
    definition:
      "The simplified centerline-to-centerline spacing between adjacent coil contacts within a coil building block, treated as even spacing for calculator estimates even when the physical contacts are not perfectly evenly distributed.",
  },
  contactLayout: {
    title: "Contact Layout",
    definition:
      "The reference description of all coil contact positions on a bottom end, including edge offsets for the first and last contacts and center-to-center spacing between adjacent contacts.",
  },
  firstContactEdgeOffset: {
    title: "First Contact Edge Offset",
    definition:
      "The distance from one edge of a bottom end to the center of the first coil contact.",
  },
  lastContactEdgeOffset: {
    title: "Last Contact Edge Offset",
    definition:
      "The distance from the opposite edge of a bottom end to the center of the last coil contact.",
  },
  contactCenterSpacing: {
    title: "Contact Center Spacing",
    definition:
      "The center-to-center distance between adjacent coil contacts in a bottom end contact layout.",
  },
  bottomEndWidth: {
    title: "Bottom End Width",
    definition:
      "The full physical width of a bottom end, used as the coil coverage contribution of that coil building block.",
  },
  standardEquipmentCatalogue: {
    title: "Standard Equipment Catalogue",
    definition:
      "The limited list of available power sources, coil frames, and bottom ends used to generate buildable catalogue-matched coil configurations.",
  },
  catalogueMaintenance: {
    title: "Catalogue Maintenance",
    definition:
      "The occasional engineering activity of keeping the standard equipment catalogue aligned with available power sources, coil frames, and bottom ends.",
  },
  recommendedSetup: {
    title: "Recommended Setup",
    definition:
      "The selected power source and standard coil configuration recommended for a field joint heating case.",
  },
  recommendationStatus: {
    title: "Recommendation Status",
    definition: "The classification applied to a recommended setup.",
  },
  recommended: {
    title: "Recommended",
    definition:
      "A recommendation status indicating that the setup fits the field joint, stays comfortably within operating limits, and is expected to heat effectively.",
  },
  marginal: {
    title: "Marginal",
    definition:
      "A recommendation status indicating that the setup is the best available standard option but should be reviewed because the power match or fit is close to, or outside, a preferred operating boundary.",
  },
  notSupported: {
    title: "Not Supported",
    definition:
      "A recommendation status reserved for cases where a recommendation cannot be calculated from valid inputs, not for poor catalogue fit or poor power utilization.",
  },
  customCoilDesignReview: {
    title: "Custom Coil Design Review",
    definition:
      "An advisory action raised when the best available standard coil configuration is a poor fit and a custom coil should be considered.",
  },
  recommendationReason: {
    title: "Recommendation Reason",
    definition:
      "The explanation for why a recommended setup received its recommendation status.",
  },
  advisoryCause: {
    title: "Advisory Cause",
    definition:
      "A structured code and message identifying a specific concern with the recommended setup, such as poor coil fit, low power utilization, or output limiting.",
  },
  advisoryAction: {
    title: "Advisory Action",
    definition:
      "A recommended follow-up action linked to one or more advisory causes, such as reviewing coil fit or requesting a custom coil design.",
  },
  fieldJointGeometry: {
    title: "Field Joint Geometry",
    definition:
      "The entered pipe and factory coating dimensions used to check coil fit and estimate heating time.",
  },
  pipeOutsideDiameter: {
    title: "Pipe Outside Diameter",
    definition: "The nominal external diameter of the pipe at the field joint.",
  },
  pipeWallThickness: {
    title: "Pipe Wall Thickness",
    definition:
      "The nominal thickness of the pipe wall at the field joint.",
  },
  factoryCoating: {
    title: "Factory Coating",
    definition:
      "The existing coating adjacent to the field joint that affects coil fit and required coverage.",
  },
  factoryCoatingThickness: {
    title: "Factory Coating Thickness",
    definition:
      "The radial thickness of the factory coating, used with pipe outside diameter to estimate the outside diameter over the coating.",
  },
  factoryCoatingOutsideDiameter: {
    title: "Factory Coating Outside Diameter",
    definition:
      "The calculated outside diameter over the factory coating, used to check whether a coil configuration fits.",
  },
  factoryCoatingCutback: {
    title: "Factory Coating Cutback",
    definition:
      "The axial distance from the weld centerline to the factory coating edge on one side of the field joint.",
  },
  sideACutback: {
    title: "Side A Cutback",
    definition:
      "The factory coating cutback measured on side A of the weld centerline.",
  },
  sideBCutback: {
    title: "Side B Cutback",
    definition:
      "The factory coating cutback measured on side B of the weld centerline.",
  },
  bareSteelLength: {
    title: "Bare Steel Length",
    definition:
      "The calculated axial length of exposed bare steel at the field joint, based on side A cutback plus side B cutback.",
  },
  coilClearance: {
    title: "Coil Clearance",
    definition:
      "A single global fixed radial gap between the factory coating and the coil configuration needed to avoid physical clash.",
  },
  coilInsideDiameter: {
    title: "Coil Inside Diameter",
    definition:
      "The minimum internal diameter required for the coil configuration to fit around the factory coating outside diameter with coil clearance on both sides.",
  },
  coilFrameSize: {
    title: "Coil Frame Size",
    definition:
      "The standard coil frame reference size, expressed in inches and named for the pipe diameter it is intended to support, not the measured physical frame diameter.",
  },
  numberOfTurns: {
    title: "Number of Turns",
    definition: "The count of coil wraps in a standard coil configuration.",
  },
  coilFrameInsideDiameter: {
    title: "Coil Frame Inside Diameter",
    definition:
      "The measured internal diameter of a coil frame, expressed in mm and used to check whether the frame fits over the factory coating outside diameter with coil clearance.",
  },
  tieBarDiameter: {
    title: "Tie Bar Diameter",
    definition:
      "The global diameter of the tie bars that sit flush with the coil frame inside diameter and set the coil cable inward from the frame.",
  },
  effectiveCoilInsideDiameter: {
    title: "Effective Coil Inside Diameter",
    definition:
      "The usable internal diameter at the coil cable position after accounting for tie bar diameter, used for physical fit checks against the factory coating outside diameter and coil clearance.",
  },
  turnSpacing: {
    title: "Turn Spacing",
    definition:
      "The distance between adjacent coil turns in a standard coil configuration.",
  },
  coilSection: {
    title: "Coil Section",
    definition:
      "The span from the first coil contact centerline to the last coil contact centerline, derived from bottom end width minus first and last contact edge offsets.",
  },
  heatingLength: {
    title: "Heating Length",
    definition:
      "The pipe length represented in the heating cycle time estimate, based on the selected coil configuration.",
  },
  requiredOverhang: {
    title: "Required Overhang",
    definition:
      "The minimum amount of coil coverage required beyond each side of the bare steel length.",
  },
  coilCoverage: {
    title: "Coil Coverage",
    definition:
      "The physical span covered by the selected coil configuration across the field joint area.",
  },
  powerMatch: {
    title: "Power Match",
    definition:
      "The compatibility between a power source and coil configuration for a specific field joint heating case.",
  },
  deliveredPower: {
    title: "Delivered Power",
    definition:
      "The useful heating power expected from the selected power source and coil configuration, expressed in kW.",
  },
  powerUtilization: {
    title: "Power Utilization",
    definition:
      "The case-specific calculated proportion of a power source's apparent power rating expected to be delivered as useful heating power for a field joint heating case.",
  },
  operatingLimit: {
    title: "Operating Limit",
    definition:
      "A stated boundary within which a power source, coil configuration, or recommended setup is considered usable.",
  },
  heatingCycleTime: {
    title: "Heating Cycle Time",
    definition:
      "The estimated time required for the selected setup to raise the heated zone from starting temperature to target heat temperature.",
  },
  targetHeatTemperature: {
    title: "Target Heat Temperature",
    definition:
      "The required measured surface temperature that the field joint is intended to reach at power-off.",
  },
  startingTemperature: {
    title: "Starting Temperature",
    definition:
      "The measured surface temperature before the heating cycle begins, which may default from ambient temperature.",
  },
  ambientTemperature: {
    title: "Ambient Temperature",
    definition:
      "The surrounding air temperature for the job environment.",
  },
  applicationProduct: {
    title: "Application Product",
    definition:
      "The optional product applied after heating, used only when its process temperature affects the recommendation or cooldown estimate.",
  },
  cooldownTime: {
    title: "Cooldown Time",
    definition:
      "An optional estimate of how long the field joint takes to cool to a subsequent process temperature when that temperature is known.",
  },
  heatSoak: {
    title: "Heat Soak",
    definition:
      "The retained heat in the pipe wall after the heating cycle, affected by heating cycle time, pipe wall thickness, and power source frequency.",
  },
  measurementConvention: {
    title: "Measurement Convention",
    definition:
      "The agreed metric measurement language for field joint inputs and calculated outputs.",
  },
  preferredTurnSpacing: {
    title: "Preferred Turn Spacing",
    definition:
      "The target center-to-center distance between adjacent coil turns used when calculating the optimum coil recommendation.",
  },
  minimumTurns: {
    title: "Minimum Turns",
    definition:
      "The lower bound on the number of coil turns considered when searching for an optimum coil configuration.",
  },
  maximumTurns: {
    title: "Maximum Turns",
    definition:
      "The upper bound on the number of coil turns considered when searching for an optimum coil configuration.",
  },
  lowUtilizationThreshold: {
    title: "Low Utilization Threshold",
    definition:
      "The minimum acceptable power utilization fraction below which a recommended setup is flagged as a low-utilization advisory cause.",
  },
  coverageMargin: {
    title: "Coverage Margin",
    definition:
      "The minimum additional coil coverage beyond the required coverage before a coil configuration is flagged as marginal on coverage.",
  },
  fitMargin: {
    title: "Fit Margin",
    definition:
      "The minimum additional effective coil inside diameter beyond the required coil inside diameter before a coil frame is flagged as marginal on fit.",
  },
  powerBoundaryMargin: {
    title: "Power Boundary Margin",
    definition:
      "The margin from the power source operating limit boundaries within which a power match is classified as marginal on power utilization.",
  },
};
